import { useTranslation } from "react-i18next";
import GlobalBanner from "~/features/navigation/components/TopBannersSection/GlobalBanner/GlobalBanner";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { SALES_CAMPAIGN_PAGE } from "~/utils/url/staticUrls";
import {
  getRemainingTimeLabel,
  isValidPageForSalesBanner,
  useActiveCampaignsNotInSmartEdit,
} from "~/features/navigation/components/TopBannersSection/utils";
import { useSchaefflerTopBannerList } from "~/features/cms/components/schaefflerTopBanner/SchaefflerTopBannerComponent";
import type { SchaefflerTopBannerData } from "~/features/cms/components/schaefflerTopBanner/SchaefflerTopBannerComponent";
import { Box, Button, Typography, styled } from "~/common/mui";
import Parser from "html-react-parser";

const StyledBannerContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  alignItems: "center",
}));

const BannerText = styled(Typography)(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: 700,
  letterSpacing: "0.5px",
}));

const BannerButton = styled(Button, { shouldForwardProp: () => true })(({ theme }) => ({
  ...theme.typography.titleSmall,
  color: `${theme.palette.primary.main} !important`,
  background: `${theme.palette.common.white} !important`,
  [theme.breakpoints.down("tablet")]: {
    height: 40,
    whiteSpace: "nowrap",
  },
}));

const BannerIcon = styled("img")({
  width: 32,
  height: 32,
  objectFit: "contain",
  objectPosition: "center",
});

export const TopBannersSection = ({ cmsBanners }: { cmsBanners?: SchaefflerTopBannerData[] } = {}) => {
  const { t } = useTranslation();
  const registeredBanners = useSchaefflerTopBannerList();
  const smartEditBanners = cmsBanners ?? registeredBanners;
  const campaignsNotInSmartEdit = useActiveCampaignsNotInSmartEdit();

  if (!isValidPageForSalesBanner(window.location.pathname)) {
    return null;
  }

  if (!smartEditBanners.length && !campaignsNotInSmartEdit.length) {
    // Nothing to show
    return null;
  }

  const smartEditBannerComponents = smartEditBanners.map((banner) => {
    const icon = banner.iconUrl ? <BannerIcon src={banner.iconUrl} /> : undefined;
    const content: JSX.Element[] = banner.content ? (Parser(banner.content) as JSX.Element[]) : [];
    let remainingTimeLabel = "";
    if (banner.showRemainingTime && banner.campaign) {
      remainingTimeLabel = getRemainingTimeLabel(t, banner.campaign.endDate);
    }
    return (
      <GlobalBanner key={banner.uid} icon={icon}>
        <Box sx={{ display: "flex", gap: 0.5, ...({} as Record<string, unknown>) }}>
          {content} {remainingTimeLabel}
        </Box>
      </GlobalBanner>
    );
  });

  let salesCampaignBanner = undefined;
  if (campaignsNotInSmartEdit.length) {
    const activeCampaign = campaignsNotInSmartEdit[0];
    salesCampaignBanner = (
      <GlobalBanner
        sx={{
          bgcolor: "primary.main",
          color: "common.white",
          cursor: "pointer",
          px: 1,
        }}
        icon={<SaleTag size={32} />}
        onClick={() => {
          window.location.href = `${SALES_CAMPAIGN_PAGE}`;
        }}
      >
        <StyledBannerContent>
          <BannerText>{t("sales.campaign.top.banner.text")}</BannerText>
          <BannerButton>{t("sales.campaign.top.banner.btn")}</BannerButton>
        </StyledBannerContent>
      </GlobalBanner>
    );
  }

  return (
    <div className="scha__root-sales-banner">
      {smartEditBannerComponents}
      {salesCampaignBanner}
    </div>
  );
};
